import { INTRO_OFFERS_ENABLED, PRIMARY_CHECKOUT_PATH } from "@/lib/introPassPath";
import { useMembershipOffer } from "@/hooks/useMembershipOffer";
import { signOutDestination } from "@/lib/signOutDestination";
import { Button } from "@/components/ui/button";
import { BRAND_LOGO, BRAND_LOGO_2X } from "@/lib/brandLogo";
import { Menu, X, ChevronDown, MessageCircle, Mail, Brain, Users, Map, Phone, PenLine, TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { capitalizeName } from "@/lib/utils";
import { useLocalPriceLabel } from "@/hooks/useLocalPriceLabel";
import {
  useIntroPassPlan,
  introPassPriceINR,
  INTRO_PASS_CHECKOUT_PATH,
} from "@/hooks/useIntroPassPlan";

import { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Home, User as UserIcon, CreditCard, LogOut, Settings, LayoutDashboard } from "lucide-react";
import { useNavigate, useLocation } from "@/lib/router-compat";
// Use the same public logo file that the PWA manifest uses
const lxLogo = BRAND_LOGO;
const lxLogo2x = BRAND_LOGO_2X;
import { useAccountMenuAccess } from "@/hooks/useAccountMenuAccess";
import { useHasActiveSubscription } from "@/hooks/useSubscription";
import { AccountMenuContent } from "@/components/AccountMenuContent";
import { openCoachKai } from "@/lib/coachKai";

interface ChildProfile {
  id: string;
  child_name: string;
  child_age: number;
}

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [childProfile, setChildProfile] = useState<ChildProfile | null>(null);
  const [parentName, setParentName] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, hasAdminAccess, hasPartnerAccess } = useAccountMenuAccess(user?.id);
  const { isAnnualSubscription } = useHasActiveSubscription(user?.id);
  const parentDashboardLocked = !isAnnualSubscription && !hasAdminAccess;
  // The pass CTA price comes from the admin-configured all-access plan and is
  // shown in the visitor's own currency, never a hardcoded rupee amount.
  const { pass } = useIntroPassPlan();
  const { label: priceLabel } = useLocalPriceLabel();
  const passPrice = introPassPriceINR(pass);
  const membershipOffer = useMembershipOffer();
  const passCtaLabel = INTRO_OFFERS_ENABLED
    ? `Explore all 20 courses for ${priceLabel(passPrice ?? 99)}`
    : `Get Annual for ${membershipOffer.annualLabel}`;

  const isLandingPage = location.pathname === "/";
  // Separate effect for fetching user data when user changes
  useEffect(() => {
    if (!user?.id) {
      setChildProfile(null);
      setParentName("");
      return;
    }
    let cancelled = false;
    fetchChildProfile(user.id, () => cancelled);
    fetchParentProfile(user.id, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setUser(session?.user ?? null);
    });

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) setUser(session?.user ?? null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchChildProfile = async (userId: string, isCancelled: () => boolean) => {
    try {
      const { data, error } = await supabase
        .from('children')
        .select('id, child_name, age')
        .eq('parent_id', userId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (isCancelled()) return;
      if (isCancelled()) return;
      if (!error && data) {
        setChildProfile({
          id: data.id,
          child_name: capitalizeName(data.child_name),
          child_age: data.age,
        });
      }

    } catch (error) {
      console.error("Error fetching child profile:", error);
    }
  };

  const fetchParentProfile = async (userId: string, isCancelled: () => boolean) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();

      if (isCancelled()) return;
      if (!error && data && data.full_name) {
        // Extract first name from full name
        const firstName = data.full_name.split(' ')[0];
        setParentName(firstName);
      }
    } catch (error) {
      console.error("Error fetching parent profile:", error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(signOutDestination());
  };

  const userInitial = parentName ? parentName.charAt(0).toUpperCase() : (user?.email?.charAt(0).toUpperCase() || "L");
  const userEmail = user?.email || "";

  return (
    <header className={`sticky top-0 z-50 w-full ${isLandingPage ? 'bg-card shadow-sm' : 'bg-transparent'}`}>
      <div className="container mx-auto px-5">
        <div className="flex h-20 items-center justify-between">
          <a href="/" className="flex items-center gap-2" aria-label="LeadXkids Home" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
            <img loading="lazy" decoding="async" src={lxLogo} srcSet={`${lxLogo} 1x, ${lxLogo2x} 2x`} width={96} height={96} alt="" className="h-12 w-auto" aria-hidden="true" />
            <span className="text-xl font-extrabold bg-gradient-hero bg-clip-text text-transparent">
              LeadXkids
            </span>
          </a>
          
          <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-4 xl:gap-5">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              Home
            </a>
            <a href="/#how-it-works" className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              What We Offer
            </a>
            <a href="/#your-childs-success" className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              Your Child's Success
            </a>
            <a href="/#successful-traits" className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              Traits for Success
            </a>
            <a href="/#about" className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              About
            </a>





            
            {/* Contact Us with hover dropdown */}
            <div className="relative group">
              <a href="/#contact" className="text-sm font-medium text-foreground hover:text-primary transition-colors cursor-pointer whitespace-nowrap">
                Contact
              </a>
              <div className="absolute top-full right-0 mt-2 w-56 bg-background border border-border/40 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-3 space-y-2">
                  <a 
                    href="https://wa.me/917619397430" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-foreground hover:text-success transition-colors py-1.5"
                  >
                    <MessageCircle className="w-4 h-4 text-success" />
                    <span>+91-7619397430</span>
                  </a>
                  <a 
                    href="mailto:hello@leadxkids.com"
                    className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors py-1.5"
                  >
                    <Mail className="w-4 h-4 text-primary" />
                    <span>hello@leadxkids.com</span>
                  </a>
                </div>
              </div>
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 hover:opacity-80 transition-opacity focus:outline-none">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-primary-foreground font-bold shadow-md text-sm">
                    {userInitial}
                  </div>
                  <span className="hidden md:block font-medium text-sm text-foreground">{parentName || "Parent"}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </DropdownMenuTrigger>
                <AccountMenuContent
                  displayName={parentName || "Parent"}
                  email={userEmail}
                  childFirstName={childProfile?.child_name}
                  hasAdminAccess={hasAdminAccess}
                  isAdmin={isAdmin}
                  isInfluencer={hasPartnerAccess}
                  onSignOut={handleSignOut}
                />
              </DropdownMenu>
            ) : (
              <>
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-4 shadow-md whitespace-nowrap"
                  onClick={() => navigate(PRIMARY_CHECKOUT_PATH)}
                >
                  {passCtaLabel}
                </Button>
                <Button 
                  size="sm"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/80 font-semibold px-4"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Sign In
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/40 bg-background/95 backdrop-blur-sm shadow-lg rounded-b-lg">
            <nav className="flex flex-col gap-2 px-2">
              {user ? (
                <>
                  {/* User info header */}
                  <div className="flex items-center gap-3 px-2.5 py-1.5 mb-2 bg-muted/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-red-500 flex items-center justify-center text-white font-bold shadow-md">
                      {userInitial}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-foreground">{parentName || "Parent"}</span>
                      <span className="text-xs text-muted-foreground">{userEmail}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigate("/");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <Home className="h-4 w-4" />
                    Home
                  </button>

                  <button
                    onClick={() => {
                      navigate("/assessment");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <Brain className="h-4 w-4" />
                    {childProfile?.child_name.split(' ')[0] || "Child"}'s Personality
                  </button>

                  <button
                    onClick={() => {
                      navigate("/learning-journeys");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <Map className="h-4 w-4" />
                    {childProfile ? `${childProfile.child_name.split(' ')[0]}'s Journey` : "Journey"}
                  </button>

                  <button
                    onClick={() => {
                      openCoachKai();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-semibold text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Ask Coach Kai
                  </button>


                  {!parentDashboardLocked && (
                    <button
                      onClick={() => {
                        navigate("/parent-dashboard");
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span className="flex-1 text-left">Parent Hub</span>
                    </button>
                  )}

                  <div className="h-px bg-border/40 my-2" />

                  <button
                    onClick={() => {
                      navigate("/profile");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <UserIcon className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate("/subscription");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg"
                  >
                    <CreditCard className="h-4 w-4" />
                    Membership
                  </button>

                  {hasAdminAccess && (
                    <>
                      <div className="h-px bg-border/40 my-2" />
                      <button
                        onClick={() => {
                          navigate("/admin/courses");
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-3 text-sm font-semibold text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg bg-muted"
                      >
                        <Settings className="h-4 w-4" />
                        {isAdmin ? "Admin Dashboard" : "Course Creator Dashboard"}
                      </button>
                    </>
                  )}

                  {false && hasPartnerAccess && (
                    <button
                      onClick={() => {
                        navigate("/partner");
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 text-sm font-semibold text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg bg-muted"
                    >
                      <TrendingUp className="h-4 w-4" />
                      Partner Portal
                    </button>
                  )}

                  <div className="h-px bg-border/40 my-2" />

                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors py-3 px-3 rounded-lg"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <a href="/#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg">
                    What We Offer
                  </a>
                  <a href="/#your-childs-success" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg">
                    Your Child's Success
                  </a>
                  <a href="/#successful-traits" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg">
                    Traits of a Successful Child
                  </a>
                  <a href="/#about" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg">
                    About
                  </a>
                  <a href="/#lx-pricing" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-3 rounded-lg">
                    Membership
                  </a>

                  
                  {/* Contact section */}
                  <div className="py-2 px-3">
                    <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 hover:text-primary">Contact</a>
                    <a 
                      href="https://wa.me/917619397430" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm text-foreground hover:text-success transition-colors py-2"
                    >
                      <MessageCircle className="w-4 h-4 text-success" />
                      <span>+91-7619397430</span>
                    </a>
                    <a 
                      href="mailto:hello@leadxkids.com"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors py-2"
                    >
                      <Mail className="w-4 h-4 text-primary" />
                      <span>hello@leadxkids.com</span>
                    </a>
                  </div>
                  <div className="h-px bg-border/40 my-2" />
                  <div className="flex flex-col gap-3 px-3">
                    <Button
                      size="sm"
                      className="bg-success text-success-foreground hover:bg-success/90 font-semibold w-full"
                      onClick={() => {
                        navigate(PRIMARY_CHECKOUT_PATH);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {passCtaLabel}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-2 border-primary text-primary w-full"
                      onClick={() => {
                        navigate("/auth");
                        setMobileMenuOpen(false);
                      }}
                    >
                      Sign In
                    </Button>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
